import type { ReactNode } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import TooltipIcon from "@/v2-ui/tooltip/TooltipIcon"
import Link from "@/v2-backoffice-client/components/link"
import type { InfoGridField } from "./infoGrid.types"

type InfoGridItemProps<TData> = InfoGridField<TData> & {
  data: TData | null | undefined
  isLoading?: boolean
}

function InfoGridItem<TData>(props: InfoGridItemProps<TData>) {
  const {
    label,
    description,
    accessor,
    data,
    show = true,
    isLoading,
    renderLabel,
    renderValue,
    to,
    onClick,
    className,
    labelClassName,
    valueClassName,
  } = props

  const shouldShow =
    typeof show === "function" ? (data ? show(data) : true) : show
  if (!shouldShow) return null

  const value = data
    ? typeof accessor === "function"
      ? accessor(data)
      : data[accessor]
    : undefined

  const labelContent = renderLabel
    ? renderLabel({ data: data as TData, field: props })
    : label

  // A described field gets the info icon after its label; hovering it
  // explains the field. The icon carries the tooltip, not the label, so
  // the label stays plain text.
  const describedLabel = description ? (
    <span className="info-grid-item-label-text">
      {labelContent}
      <TooltipIcon
        iconSrc="info"
        iconSize={12}
        iconClassName="info-grid-item-tooltip-icon"
        content={<span className="block px-3 py-2">{description}</span>}
        contentSide="top"
        contentClassName="ui-tooltip-content--panel max-w-[280px]"
      />
    </span>
  ) : (
    labelContent
  )

  let valueContent: ReactNode = null

  if (isLoading) {
    valueContent = <div className="info-grid-item-value-loading" />
  } else if (renderValue && data) {
    const rendered = renderValue({ data, value })
    valueContent = rendered !== null && rendered !== undefined ? rendered : "—"
  } else if (value !== null && value !== undefined && value !== "") {
    valueContent = value as ReactNode
  } else {
    valueContent = "—"
  }

  // Wrap in link or button if specified
  if (!isLoading && data && (to || onClick)) {
    const hasValue =
      valueContent !== null &&
      valueContent !== undefined &&
      valueContent !== "" &&
      valueContent !== "—"

    if (to && hasValue) {
      const href = typeof to === "function" ? to({ data, value }) : to
      if (href) {
        valueContent = <Link to={href}>{valueContent}</Link>
      }
    } else if (onClick && hasValue) {
      valueContent = (
        <button
          type="button"
          className="info-grid-item-value ui-link"
          onClick={() => {
            onClick(data)
          }}
        >
          {valueContent}
        </button>
      )
    }
  }

  return (
    <div className={cn("info-grid-item", className)}>
      <dt className={cn("info-grid-item-label", labelClassName)}>
        {describedLabel}
      </dt>
      <dd className={cn("info-grid-item-value", valueClassName)}>
        {valueContent}
      </dd>
    </div>
  )
}

export default InfoGridItem
